/**
 * Pool of high-contrast vector silhouettes for the Ghost Images rescue module.
 * Each shape is rendered as inner SVG markup inside a 300x300 viewBox.
 * Shapes use white strokes / partial fills against a black background so the
 * eye produces a strong cyan/violet afterimage when the source is removed.
 *
 * Designed to:
 *  - have a clear silhouette (recognizable from a fading ghost image)
 *  - leave internal negative space (so the afterimage is patterned, not a blob)
 *  - avoid fine detail that disappears at low retinal contrast
 */
export interface GhostShape {
  readonly id: string;
  readonly name: string;
  readonly inner: string;
}

const W = 300;
const C = W / 2;
const STROKE = 16;

function ring(r: number): string {
  return `<circle cx="${C}" cy="${C}" r="${r}" fill="none" stroke="white" stroke-width="${STROKE}"/>`;
}

export const SHAPES: readonly GhostShape[] = [
  {
    id: 'target',
    name: 'Target',
    inner: `${ring(120)}${ring(78)}<circle cx="${C}" cy="${C}" r="22" fill="white"/>`,
  },
  {
    id: 'triangle',
    name: 'Triangle',
    inner: `<polygon points="150,30 270,255 30,255" fill="none" stroke="white" stroke-width="22" stroke-linejoin="round"/>`,
  },
  {
    id: 'window',
    name: 'Window',
    inner: `<rect x="40" y="40" width="220" height="220" fill="none" stroke="white" stroke-width="20"/>` +
           `<line x1="150" y1="40" x2="150" y2="260" stroke="white" stroke-width="14"/>` +
           `<line x1="40" y1="150" x2="260" y2="150" stroke="white" stroke-width="14"/>`,
  },
  {
    id: 'plus',
    name: 'Plus',
    inner: `<rect x="125" y="40" width="50" height="220" fill="white"/>` +
           `<rect x="40" y="125" width="220" height="50" fill="white"/>`,
  },
  {
    id: 'crosshair',
    name: 'Crosshair',
    inner: `${ring(110)}` +
           `<line x1="150" y1="20" x2="150" y2="90" stroke="white" stroke-width="14"/>` +
           `<line x1="150" y1="210" x2="150" y2="280" stroke="white" stroke-width="14"/>` +
           `<line x1="20" y1="150" x2="90" y2="150" stroke="white" stroke-width="14"/>` +
           `<line x1="210" y1="150" x2="280" y2="150" stroke="white" stroke-width="14"/>` +
           `<circle cx="${C}" cy="${C}" r="14" fill="white"/>`,
  },
  {
    id: 'star',
    name: 'Star',
    inner: `<polygon points="150,30 183,118 276,118 201,172 230,260 150,206 70,260 99,172 24,118 117,118" fill="none" stroke="white" stroke-width="16" stroke-linejoin="round"/>`,
  },
  {
    id: 'hexagon',
    name: 'Hexagon',
    inner: `<polygon points="150,30 260,90 260,210 150,270 40,210 40,90" fill="none" stroke="white" stroke-width="20" stroke-linejoin="round"/>` +
           `<polygon points="150,90 210,120 210,180 150,210 90,180 90,120" fill="none" stroke="white" stroke-width="12" stroke-linejoin="round"/>`,
  },
  {
    id: 'diamond',
    name: 'Diamond',
    inner: `<polygon points="150,25 275,150 150,275 25,150" fill="none" stroke="white" stroke-width="20" stroke-linejoin="round"/>` +
           `<polygon points="150,90 210,150 150,210 90,150" fill="white"/>`,
  },
  {
    id: 'arrow',
    name: 'Arrow',
    inner: `<polygon points="150,30 260,150 200,150 200,270 100,270 100,150 40,150" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>`,
  },
  {
    id: 'spiral',
    name: 'Spiral',
    inner: `${ring(120)}${ring(80)}${ring(40)}` +
           `<rect x="148" y="0" width="4" height="${C}" fill="black"/>`,
  },
  {
    id: 'ring',
    name: 'Ring',
    inner: `<circle cx="${C}" cy="${C}" r="115" fill="white"/>` +
           `<circle cx="${C}" cy="${C}" r="60" fill="black"/>`,
  },
  {
    id: 'bars',
    name: 'Bars',
    inner: `<rect x="40" y="40"  width="220" height="34" fill="white"/>` +
           `<rect x="40" y="115" width="220" height="34" fill="white"/>` +
           `<rect x="40" y="190" width="220" height="34" fill="white"/>`,
  },
  {
    id: 'house',
    name: 'House',
    inner: `<polygon points="150,30 270,140 30,140" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>` +
           `<rect x="60" y="140" width="180" height="130" fill="none" stroke="white" stroke-width="18"/>` +
           `<rect x="125" y="180" width="50" height="90" fill="white"/>`,
  },
  {
    id: 'gear',
    name: 'Gear',
    inner: `<polygon points="150,20 175,55 220,40 225,90 270,105 250,150 270,195 225,210 220,260 175,245 150,280 125,245 80,260 75,210 30,195 50,150 30,105 75,90 80,40 125,55" fill="none" stroke="white" stroke-width="14" stroke-linejoin="round"/>` +
           `${ring(50)}`,
  },
  {
    id: 'square-x',
    name: 'Cross Square',
    inner: `<rect x="40" y="40" width="220" height="220" fill="none" stroke="white" stroke-width="20"/>` +
           `<line x1="40" y1="40" x2="260" y2="260" stroke="white" stroke-width="14"/>` +
           `<line x1="260" y1="40" x2="40" y2="260" stroke="white" stroke-width="14"/>`,
  },
  {
    id: 'eye',
    name: 'Eye',
    inner: `<path d="M 30 150 Q 150 30 270 150 Q 150 270 30 150 Z" fill="none" stroke="white" stroke-width="16"/>` +
           `<circle cx="${C}" cy="${C}" r="42" fill="white"/>` +
           `<circle cx="${C}" cy="${C}" r="14" fill="black"/>`,
  },
  {
    id: 'moon',
    name: 'Crescent',
    inner: `<path d="M 210 60 A 110 110 0 1 0 210 240 A 90 90 0 1 1 210 60 Z" fill="white"/>`,
  },
  {
    id: 'lattice',
    name: 'Lattice',
    inner: `<rect x="40" y="40" width="220" height="220" fill="none" stroke="white" stroke-width="14"/>` +
           `<line x1="115" y1="40" x2="115" y2="260" stroke="white" stroke-width="10"/>` +
           `<line x1="185" y1="40" x2="185" y2="260" stroke="white" stroke-width="10"/>` +
           `<line x1="40" y1="115" x2="260" y2="115" stroke="white" stroke-width="10"/>` +
           `<line x1="40" y1="185" x2="260" y2="185" stroke="white" stroke-width="10"/>`,
  },
  {
    id: 'pentagon',
    name: 'Pentagon',
    inner: `<polygon points="150,30 270,120 225,260 75,260 30,120" fill="none" stroke="white" stroke-width="20" stroke-linejoin="round"/>`,
  },
  {
    id: 'octagon',
    name: 'Octagon',
    inner: `<polygon points="100,30 200,30 270,100 270,200 200,270 100,270 30,200 30,100" fill="none" stroke="white" stroke-width="20" stroke-linejoin="round"/>` +
           `<circle cx="${C}" cy="${C}" r="40" fill="white"/>`,
  },
  {
    id: 'heart',
    name: 'Heart',
    inner: `<path d="M 150 260 C 30 180 30 70 100 70 C 130 70 150 95 150 120 C 150 95 170 70 200 70 C 270 70 270 180 150 260 Z" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>`,
  },
  {
    id: 'cloud',
    name: 'Cloud',
    inner: `<path d="M 80 210 Q 30 210 30 165 Q 30 120 80 120 Q 95 70 150 70 Q 210 70 220 125 Q 270 125 270 165 Q 270 210 220 210 Z" fill="none" stroke="white" stroke-width="16" stroke-linejoin="round"/>`,
  },
  {
    id: 'sun',
    name: 'Sun',
    inner: `<circle cx="${C}" cy="${C}" r="60" fill="white"/>` +
           `<g stroke="white" stroke-width="14" stroke-linecap="round">` +
           `<line x1="150" y1="20"  x2="150" y2="55"/>` +
           `<line x1="150" y1="245" x2="150" y2="280"/>` +
           `<line x1="20"  y1="150" x2="55"  y2="150"/>` +
           `<line x1="245" y1="150" x2="280" y2="150"/>` +
           `<line x1="55"  y1="55"  x2="80"  y2="80"/>` +
           `<line x1="220" y1="220" x2="245" y2="245"/>` +
           `<line x1="55"  y1="245" x2="80"  y2="220"/>` +
           `<line x1="220" y1="80"  x2="245" y2="55"/>` +
           `</g>`,
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    inner: `<g stroke="white" stroke-width="10" stroke-linecap="round" fill="none">` +
           `<line x1="150" y1="20"  x2="150" y2="280"/>` +
           `<line x1="32"  y1="85"  x2="268" y2="215"/>` +
           `<line x1="32"  y1="215" x2="268" y2="85"/>` +
           `<polyline points="130,40 150,55 170,40"/>` +
           `<polyline points="130,260 150,245 170,260"/>` +
           `<polyline points="50,75 60,100 85,90"/>` +
           `<polyline points="250,225 240,200 215,210"/>` +
           `<polyline points="50,225 60,200 85,210"/>` +
           `<polyline points="250,75 240,100 215,90"/>` +
           `</g>`,
  },
  {
    id: 'chevron-stack',
    name: 'Chevrons',
    inner: `<g stroke="white" stroke-width="18" fill="none" stroke-linejoin="round" stroke-linecap="round">` +
           `<polyline points="50,80 150,140 250,80"/>` +
           `<polyline points="50,160 150,220 250,160"/>` +
           `</g>`,
  },
  {
    id: 'ladder',
    name: 'Ladder',
    inner: `<line x1="80" y1="20" x2="80" y2="280" stroke="white" stroke-width="14"/>` +
           `<line x1="220" y1="20" x2="220" y2="280" stroke="white" stroke-width="14"/>` +
           `<line x1="80" y1="80"  x2="220" y2="80"  stroke="white" stroke-width="12"/>` +
           `<line x1="80" y1="140" x2="220" y2="140" stroke="white" stroke-width="12"/>` +
           `<line x1="80" y1="200" x2="220" y2="200" stroke="white" stroke-width="12"/>` +
           `<line x1="80" y1="260" x2="220" y2="260" stroke="white" stroke-width="12"/>`,
  },
  {
    id: 'checker',
    name: 'Checker',
    inner: `<g fill="white">` +
           `<rect x="40"  y="40"  width="55" height="55"/>` +
           `<rect x="150" y="40"  width="55" height="55"/>` +
           `<rect x="95"  y="95"  width="55" height="55"/>` +
           `<rect x="205" y="95"  width="55" height="55"/>` +
           `<rect x="40"  y="150" width="55" height="55"/>` +
           `<rect x="150" y="150" width="55" height="55"/>` +
           `<rect x="95"  y="205" width="55" height="55"/>` +
           `<rect x="205" y="205" width="55" height="55"/>` +
           `</g>`,
  },
  {
    id: 'zigzag',
    name: 'Zigzag',
    inner: `<polyline points="30,90 90,140 150,90 210,140 270,90" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round" stroke-linecap="round"/>` +
           `<polyline points="30,180 90,230 150,180 210,230 270,180" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round" stroke-linecap="round"/>`,
  },
  {
    id: 'clock',
    name: 'Clock',
    inner: `${ring(115)}` +
           `<g stroke="white" stroke-width="10" stroke-linecap="round">` +
           `<line x1="150" y1="50"  x2="150" y2="70"/>` +
           `<line x1="150" y1="230" x2="150" y2="250"/>` +
           `<line x1="50"  y1="150" x2="70"  y2="150"/>` +
           `<line x1="230" y1="150" x2="250" y2="150"/>` +
           `</g>` +
           `<line x1="150" y1="150" x2="150" y2="80"  stroke="white" stroke-width="10" stroke-linecap="round"/>` +
           `<line x1="150" y1="150" x2="210" y2="170" stroke="white" stroke-width="10" stroke-linecap="round"/>` +
           `<circle cx="${C}" cy="${C}" r="10" fill="white"/>`,
  },
  {
    id: 'hourglass',
    name: 'Hourglass',
    inner: `<polygon points="50,40 250,40 150,150 250,260 50,260 150,150" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>` +
           `<line x1="50" y1="40"  x2="250" y2="40"  stroke="white" stroke-width="18" stroke-linecap="round"/>` +
           `<line x1="50" y1="260" x2="250" y2="260" stroke="white" stroke-width="18" stroke-linecap="round"/>`,
  },
  {
    id: 'arrow-up-down',
    name: 'Up/Down',
    inner: `<g fill="white">` +
           `<polygon points="100,30 150,80 130,80 130,140 70,140 70,80 50,80"/>` +
           `<polygon points="200,270 150,220 170,220 170,160 230,160 230,220 250,220"/>` +
           `</g>`,
  },
  {
    id: 'shield',
    name: 'Shield',
    inner: `<path d="M 150 30 L 260 70 L 250 180 Q 230 250 150 275 Q 70 250 50 180 L 40 70 Z" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>` +
           `<line x1="150" y1="100" x2="150" y2="220" stroke="white" stroke-width="14"/>` +
           `<line x1="100" y1="160" x2="200" y2="160" stroke="white" stroke-width="14"/>`,
  },
  {
    id: 'compass',
    name: 'Compass',
    inner: `${ring(115)}` +
           `<polygon points="150,40 175,150 150,260 125,150" fill="none" stroke="white" stroke-width="12" stroke-linejoin="round"/>` +
           `<polygon points="40,150 150,125 260,150 150,175" fill="none" stroke="white" stroke-width="12" stroke-linejoin="round"/>`,
  },
  {
    id: 'cube',
    name: 'Cube',
    inner: `<polygon points="60,90 150,40 240,90 240,210 150,260 60,210" fill="none" stroke="white" stroke-width="16" stroke-linejoin="round"/>` +
           `<polyline points="60,90 150,140 240,90" fill="none" stroke="white" stroke-width="14" stroke-linejoin="round"/>` +
           `<line x1="150" y1="140" x2="150" y2="260" stroke="white" stroke-width="14"/>`,
  },
  {
    id: 'tree',
    name: 'Tree',
    inner: `<polygon points="150,30 230,110 180,110 230,180 180,180 240,250 60,250 120,180 70,180 120,110 70,110" fill="none" stroke="white" stroke-width="14" stroke-linejoin="round"/>` +
           `<rect x="135" y="250" width="30" height="20" fill="white"/>`,
  },
  {
    id: 'crown',
    name: 'Crown',
    inner: `<polygon points="40,240 60,90 110,170 150,70 190,170 240,90 260,240" fill="none" stroke="white" stroke-width="16" stroke-linejoin="round"/>` +
           `<line x1="40" y1="240" x2="260" y2="240" stroke="white" stroke-width="14"/>` +
           `<circle cx="60"  cy="90"  r="10" fill="white"/>` +
           `<circle cx="150" cy="70"  r="10" fill="white"/>` +
           `<circle cx="240" cy="90"  r="10" fill="white"/>`,
  },
  {
    id: 'staircase',
    name: 'Staircase',
    inner: `<polyline points="30,260 30,210 90,210 90,160 150,160 150,110 210,110 210,60 270,60" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round" stroke-linecap="round"/>`,
  },
  {
    id: 'wave',
    name: 'Wave',
    inner: `<path d="M 30 130 Q 70 70 110 130 T 190 130 T 270 130" fill="none" stroke="white" stroke-width="18" stroke-linecap="round"/>` +
           `<path d="M 30 200 Q 70 140 110 200 T 190 200 T 270 200" fill="none" stroke="white" stroke-width="14" stroke-linecap="round" opacity="0.85"/>`,
  },
  {
    id: 'anchor',
    name: 'Anchor',
    inner: `<circle cx="${C}" cy="70" r="22" fill="none" stroke="white" stroke-width="14"/>` +
           `<line x1="150" y1="92"  x2="150" y2="240" stroke="white" stroke-width="16" stroke-linecap="round"/>` +
           `<line x1="100" y1="125" x2="200" y2="125" stroke="white" stroke-width="14" stroke-linecap="round"/>` +
           `<path d="M 60 200 Q 80 260 150 260 Q 220 260 240 200" fill="none" stroke="white" stroke-width="16" stroke-linecap="round"/>`,
  },
  {
    id: 'yin-yang',
    name: 'Yin Yang',
    inner: `<circle cx="${C}" cy="${C}" r="115" fill="none" stroke="white" stroke-width="6"/>` +
           `<path d="M 150 35 A 115 115 0 0 1 150 265 A 57.5 57.5 0 0 0 150 150 A 57.5 57.5 0 0 1 150 35 Z" fill="white"/>` +
           `<circle cx="150" cy="92"  r="14" fill="black"/>` +
           `<circle cx="150" cy="208" r="14" fill="white"/>`,
  },
  {
    id: 'frame-in-frame',
    name: 'Nested Frames',
    inner: `<rect x="30" y="30" width="240" height="240" fill="none" stroke="white" stroke-width="14"/>` +
           `<rect x="80" y="80" width="140" height="140" fill="none" stroke="white" stroke-width="12"/>` +
           `<rect x="125" y="125" width="50" height="50" fill="white"/>`,
  },
  {
    id: 'arrows-cross',
    name: 'Cross Arrows',
    inner: `<g stroke="white" stroke-width="14" stroke-linecap="round" stroke-linejoin="round" fill="none">` +
           `<line x1="40" y1="150" x2="260" y2="150"/>` +
           `<polyline points="70,120 40,150 70,180"/>` +
           `<polyline points="230,120 260,150 230,180"/>` +
           `<line x1="150" y1="40" x2="150" y2="260"/>` +
           `<polyline points="120,70 150,40 180,70"/>` +
           `<polyline points="120,230 150,260 180,230"/>` +
           `</g>`,
  },
  {
    id: 'lightbulb',
    name: 'Bulb',
    inner: `<path d="M 150 40 Q 80 40 80 110 Q 80 160 120 190 L 120 220 L 180 220 L 180 190 Q 220 160 220 110 Q 220 40 150 40 Z" fill="none" stroke="white" stroke-width="16" stroke-linejoin="round"/>` +
           `<line x1="120" y1="240" x2="180" y2="240" stroke="white" stroke-width="14" stroke-linecap="round"/>` +
           `<line x1="130" y1="260" x2="170" y2="260" stroke="white" stroke-width="14" stroke-linecap="round"/>`,
  },
  {
    id: 'kite',
    name: 'Kite',
    inner: `<polygon points="150,30 250,140 150,270 50,140" fill="none" stroke="white" stroke-width="18" stroke-linejoin="round"/>` +
           `<line x1="150" y1="30" x2="150" y2="270" stroke="white" stroke-width="10"/>` +
           `<line x1="50" y1="140" x2="250" y2="140" stroke="white" stroke-width="10"/>`,
  },
];

export function pickShape(excludeId?: string): GhostShape {
  const pool = excludeId ? SHAPES.filter((s) => s.id !== excludeId) : SHAPES;
  const idx = Math.floor(Math.random() * pool.length);
  return pool[idx] ?? SHAPES[0]!;
}
